import { SizeProp } from "@fortawesome/fontawesome-svg-core";
import {
    faAngleDown,
    faAngleUp,
    faArrowDownAZ,
    faArrowUpAZ,
    faCaretDown,
    faCaretUp,
    faEllipsisVertical
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import _ from "lodash";
import { HTMLProps, useState } from "react";
import { useFormContext } from "react-hook-form";
import { BasicProps } from "./ComponentsInterface";
import { LBtn } from "./LBtn";
import styles from "./LDropdownMenu.module.css";

const fontAwesomeIconSize: { [key: string]: SizeProp } = {
    small: "sm",
    medium: "lg",
    large: "xl"
};

export const LDropdownMenu = ({
    id,
    options,
    dropDownInputName,
    hideErrorLabel,
    isRightAligned,
    isSelected,
    isSingular,
    label,
    labelProps,
    tag,
    theme = "grey",
    type,
    iconFamily = "angle",
    switchIconOnActive,
    iconColor,
    iconSize = "small",
    itemClasses,
    variation = "unstyled",
    noHover,
    centerContent,
    scrollable,
    toolTipText
}: DropdownProps): JSX.Element => {
    const [isActive, setActive] = useState(false);

    const {
        formState: { errors }
    } = useFormContext();

    const error = _.get(errors, dropDownInputName ?? "");
    const hasError = error !== undefined && error !== null;

    const handleButtonClick = (): void => {
        setActive(!isActive);
    };
    const classes = classNames(
        "dropdown",
        isRightAligned ? "is-right" : "",
        isActive ? `is-active ${styles.customDropdown}` : ""
    );
    const contentClasses = classNames(
        "dropdown-content",
        styles.customDropdownContent,
        scrollable ? " scrollable" : ""
    );
    const itemClassesNames = classNames(
        "dropdown-item",
        noHover ? "" : styles.customDropdownItem,
        centerContent ? "has-text-centered" : "",
        itemClasses
    );

    return (
        <>
            <div className="field">
                {tag && <label className="label is-small">{tag}</label>}
                <div className={classes} role="menu">
                    <div className="dropdown-trigger">
                        <LBtn
                            fullWidth
                            onClick={handleButtonClick}
                            ariaControls={id}
                            ariaPopup
                            theme={theme}
                            notRounded
                            isActive={isActive || isSelected}
                            variation={variation}
                            toolTipText={toolTipText}
                        >
                            {label && <span {...labelProps}>{label}</span>}
                            <span className={`icon is-${iconSize}`}>
                                {type === "sort" ? (
                                    <FontAwesomeIcon
                                        icon={isActive ? faArrowUpAZ : faArrowDownAZ}
                                        size={fontAwesomeIconSize[iconSize]}
                                        color={iconColor}
                                    />
                                ) : type === "tableRowMenu" ? (
                                    <FontAwesomeIcon
                                        icon={faEllipsisVertical}
                                        color={iconColor}
                                        size={fontAwesomeIconSize[iconSize]}
                                    />
                                ) : iconFamily === "angle" ? (
                                    <FontAwesomeIcon
                                        icon={switchIconOnActive && isActive ? faAngleUp : faAngleDown}
                                        size={fontAwesomeIconSize[iconSize]}
                                        color={iconColor}
                                    />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={switchIconOnActive && isActive ? faCaretUp : faCaretDown}
                                        size={fontAwesomeIconSize[iconSize]}
                                        color={iconColor}
                                    />
                                )}
                            </span>
                        </LBtn>
                    </div>
                    <div className="dropdown-menu" role="menu" id={id}>
                        <div className={contentClasses}>
                            {options.map((o, index) => (
                                <div
                                    className={itemClassesNames + (o.classes ? o.classes : "")}
                                    style={o.disabled ? { cursor: "not-allowed" } : undefined}
                                    key={index}
                                    onClick={() => {
                                        if (!o.disabled && isSingular) {
                                            handleButtonClick();
                                        }
                                    }}
                                >
                                    {o.children}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                {!hideErrorLabel && (
                    <p className={`help is-danger ${hasError ? "" : "is-hidden-custom"}`}>* Required</p>
                )}
            </div>
            {isActive && <div className={styles.dropdownOverlay} onClick={handleButtonClick} />}
        </>
    );
};

export interface DropdownMenuOption extends BasicProps {
    optionName: string;
    classes?: string;
    disabled?: boolean;
}

export interface DropdownCommonProps {
    options: DropdownMenuOption[];
    label?: string;
    labelProps?: HTMLProps<HTMLSpanElement>;
    id: string;
    isSelected?: boolean;
    dropDownInputName?: string;
    tag?: string;
    hideErrorLabel?: boolean;
    isRightAligned?: boolean;
    isSingular?: boolean;
    theme?: "dark" | "light" | "grey";
    iconColor?: string;
    iconSize?: "small" | "medium" | "large";
    itemClasses?: string;
    variation?: "" | "unstyled" | "outline" | "secondary" | "select";
    noHover?: boolean;
    centerContent?: boolean;
    scrollable?: boolean;
    toolTipText?: string | JSX.Element;
}

export type DropdownConditionalProps =
    | {
          iconFamily?: "angle" | "caret";
          switchIconOnActive?: boolean;
          type?: "button";
      }
    | {
          iconFamily?: never;
          switchIconOnActive?: true;
          type?: "sort";
      }
    | {
          iconFamily?: never;
          switchIconOnActive?: never;
          type?: "tableRowMenu";
      };

export type DropdownProps = DropdownCommonProps & DropdownConditionalProps;
